import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import TrustIndicators from "@/components/TrustIndicators";
import BeforeAfterComparison from "@/components/BeforeAfterComparison";
import Features from "@/components/Features";
import CRMIntegrationShowcase from "@/components/CRMIntegrationShowcase";
import { Suspense, lazy } from "react";
const CRMIntegrationHub = lazy(() => import("@/components/CRMIntegrationHub"));
const CloudPartnersShowcase = lazy(() => import("@/components/CloudPartnersShowcase"));
import UseCases from "@/components/UseCases";
import InteractiveAudioPlayer from "@/components/InteractiveAudioPlayer";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logo from "@/assets/holionix-logo.jpeg";
import { organizationSchema, productSchema } from "@/utils/structuredData";
import { track } from "@/utils/analytics";
import { useTranslation } from 'react-i18next';

const Index = () => {
  const { t } = useTranslation();
  
  const comparisonItems = [
    {
      problem: t('comparison.problem_1'),
      solution: t('comparison.solution_1')
    },
    {
      problem: t('comparison.problem_2'),
      solution: t('comparison.solution_2')
    },
    {
      problem: t('comparison.problem_3'),
      solution: t('comparison.solution_3')
    },
    {
      problem: t('comparison.problem_4'),
      solution: t('comparison.solution_4')
    },
    {
      problem: t('comparison.problem_5'),
      solution: t('comparison.solution_5')
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema, productSchema]
        }}
      />
      <Navigation />
      <Hero />
      <TrustIndicators />
      
      {/* AI Voice Demo Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {t('voice_demo.title').split('AI Voice Agents')[0]}
              <span className="gradient-brand-text">{t('voice_demo.title').includes('AI Voice Agents') ? 'AI Voice Agents' : t('voice_demo.title').split(' ').slice(-3).join(' ')}</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('voice_demo.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <InteractiveAudioPlayer
              agentName={t('voice_demo.agent_1_name')}
              agentRole={t('voice_demo.agent_1_role')}
              avatar={logo}
              accent={t('voice_demo.agent_1_accent')}
              audioType="inbound-support"
            />
            <InteractiveAudioPlayer
              agentName={t('voice_demo.agent_2_name')}
              agentRole={t('voice_demo.agent_2_role')}
              avatar={logo}
              accent={t('voice_demo.agent_2_accent')}
              audioType="outbound-sales"
            />
            <InteractiveAudioPlayer
              agentName={t('voice_demo.agent_3_name')}
              agentRole={t('voice_demo.agent_3_role')}
              avatar={logo}
              accent={t('voice_demo.agent_3_accent')}
              audioType="marketing"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/contact">
              <Button size="lg" className="gradient-brand text-white hover:opacity-90" onClick={() => track({ name: 'cta_request_custom_voice_sample' })}>
                {t('voice_demo.request_sample')}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {t('comparison.title').split('Powerful')[0]}
              <span className="gradient-brand-text">{t('comparison.title').includes('Powerful') ? 'Powerful' : t('comparison.title').split(' ').slice(-1)[0]}</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('comparison.subtitle')}
            </p>
          </motion.div>

          <BeforeAfterComparison items={comparisonItems} />
        </div>
      </section>

      <Features />
      <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading integration hub…</div>}>
        <CRMIntegrationHub />
      </Suspense>
      <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading partners…</div>}>
        <CloudPartnersShowcase />
      </Suspense>
      <UseCases />
      
      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {t('cta.transform')}
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              {t('cta.join')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-7" onClick={() => track({ name: 'cta_book_live_demo' })}>
                  {t('cta.book_demo')}
                </Button>
              </Link>
              <Link to="/solutions">
                <Button size="lg" variant="outline" className="text-lg px-10 py-7" onClick={() => track({ name: 'cta_explore_use_cases' })}>
                  {t('cta.explore_cases')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Index;
